/* eslint-disable no-template-curly-in-string */

import axios from "axios";
import { api_gateway_conn } from "./cs-lab-config";
import STK500 from "stk500-esm";
import { ESPLoader, Transport } from "esptool-js";

const ARDUINO_COMPILER_URL = "arduino-compiler/compile";
const ESP_COMPILER_URL = "arduino-compiler/esp-compile";
// Local testing: call the ESP compiler's Lambda emulator (docker on :9000) through the
// dev-server proxy in src/setupProxy.js. Set to false to use the API Gateway route again.
const USE_LOCAL_ESP_COMPILER = false;
const ESP_COMPILER_LOCAL_URL = "/esp-compiler-local";

const SUPPORTED_BOARDS = {
    uno: {
        name: "Arduino Uno",
        fqbn: "arduino:avr:uno",
        stkConfig: { baudRate: 115200, signature: new Uint8Array([0x1e, 0x95, 0x0f]), pageSize: 128, timeout: 1000 }
    },
    nano: {
        name: "Arduino Nano (Old Bootloader)",
        fqbn: "arduino:avr:nano:cpu=atmega328old", // Crucial: 95% of cheap Nanos use the old bootloader
        stkConfig: { baudRate: 57600, signature: new Uint8Array([0x1e, 0x95, 0x0f]), pageSize: 128, timeout: 1000 }
    },
    mega: {
        name: "Arduino Mega 2560",
        fqbn: "arduino:avr:mega",
        stkConfig: { baudRate: 115200, signature: new Uint8Array([0x1e, 0x98, 0x01]), pageSize: 256, timeout: 1000 }
    },
    nodemcu: {
        name: "NodeMCU 1.0 (ESP8266)",
        platform: "esp8266",
        fqbn: "esp8266:esp8266:nodemcuv2",
        maxSketchBytes: 1044464, // Default 4MB flash layout (FS:2MB, OTA:~1019KB)
        // 115200 matches the ROM bootloader, so esptool-js never reopens the port to change speed
        espConfig: { baudRate: 115200, flashAddress: 0x0 }
    }
};

const ARDUINO_USB_FILTERS = [
    { usbVendorId: 0x2341 }, // Official Arduino LLC
    { usbVendorId: 0x2A03 }, // Arduino.org (from the company split days)
    { usbVendorId: 0x1B4F }, // SparkFun
    { usbVendorId: 0x239A }, // Adafruit
    { usbVendorId: 0x1A86 }, // QinHeng (CH340 chips - ~90% of cheap clones)
    { usbVendorId: 0x10C4 }, // Silicon Labs (CP210x - common in NodeMCU/ESP32)
    { usbVendorId: 0x0403 }, // FTDI (Older Nanos and clones)
];

function getCodeSuggestions(monacoInstance, range) {
    return [
        // --- Core Structures ---
        {
            label: "setup",
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: "void setup() {\n\t$0\n}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "The setup() function is called when a sketch starts. Use it to initialize variables, pin modes, start using libraries, etc.",
            range: range,
        },
        {
            label: "loop",
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: "void loop() {\n\t$0\n}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "After creating a setup() function, the loop() function does precisely what its name suggests, and loops consecutively.",
            range: range,
        },

        // --- Digital I/O ---
        {
            label: "pinMode",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "pinMode(${1:pin}, ${2:mode});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Configures the specified pin to behave either as an input or an output.",
            range: range,
        },
        {
            label: "digitalWrite",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "digitalWrite(${1:pin}, ${2:value});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Write a HIGH or a LOW value to a digital pin.",
            range: range,
        },
        {
            label: "digitalRead",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "digitalRead(${1:pin})",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Reads the value from a specified digital pin, either HIGH or LOW.",
            range: range,
        },

        // --- Analog I/O ---
        {
            label: "analogRead",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "analogRead(${1:pin})",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Reads the value from the specified analog pin.",
            range: range,
        },
        {
            label: "analogWrite",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "analogWrite(${1:pin}, ${2:value});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Writes an analog value (PWM wave) to a pin.",
            range: range,
        },

        // --- Time ---
        {
            label: "delay",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "delay(${1:ms});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Pauses the program for the amount of time (in milliseconds) specified as parameter.",
            range: range,
        },
        {
            label: "delayMicroseconds",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "delayMicroseconds(${1:us});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Pauses the program for the amount of time (in microseconds) specified as parameter.",
            range: range,
        },

        // --- Serial Communication ---
        {
            label: "Serial.begin",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "Serial.begin(${1:9600});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Sets the data rate in bits per second (baud) for serial data transmission.",
            range: range,
        },
        {
            label: "Serial.print",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "Serial.print(${1:val});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Prints data to the serial port as human-readable ASCII text.",
            range: range,
        },
        {
            label: "Serial.println",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "Serial.println(${1:val});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Prints data to the serial port as human-readable ASCII text followed by a carriage return character and a newline character.",
            range: range,
        },
        {
            label: "Serial.available",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "Serial.available()",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Get the number of bytes (characters) available for reading from the serial port.",
            range: range,
        },
        {
            label: "Serial.read",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "Serial.read()",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: "Reads incoming serial data.",
            range: range,
        },

        // --- Constants ---
        {
            label: "HIGH",
            kind: monacoInstance.languages.CompletionItemKind.Constant,
            insertText: "HIGH",
            documentation: "Digital pin state HIGH (usually 5V or 3.3V).",
            range: range,
        },
        {
            label: "LOW",
            kind: monacoInstance.languages.CompletionItemKind.Constant,
            insertText: "LOW",
            documentation: "Digital pin state LOW (0V / Ground).",
            range: range,
        },
        {
            label: "INPUT",
            kind: monacoInstance.languages.CompletionItemKind.Constant,
            insertText: "INPUT",
            documentation: "Sets a pin to act as an input.",
            range: range,
        },
        {
            label: "OUTPUT",
            kind: monacoInstance.languages.CompletionItemKind.Constant,
            insertText: "OUTPUT",
            documentation: "Sets a pin to act as an output.",
            range: range,
        }
    ];
}

// ─────────────────────────────────────────────────────────────────────────────
// WebSerialStream – adapts the browser Web Serial API into the Node.js-style
// duplex stream interface that stk500-esm expects (.write / .on / .once /
// .removeListener).
// ─────────────────────────────────────────────────────────────────────────────
class WebSerialStream {
    constructor(port) {
        this._port = port;
        this._writer = port.writable.getWriter();
        this._reader = port.readable.getReader();
        this._dataListeners = [];
        this._onceListeners = [];
        this._readLoop();
    }

    // Continuously pull bytes and fan-out to registered listeners
    async _readLoop() {
        try {
            while (true) {
                const { value, done } = await this._reader.read();
                if (done) break;

                // Fire persistent listeners
                this._dataListeners.forEach(fn => fn(value));

                // Fire and clear one-time listeners
                const once = this._onceListeners.splice(0);
                once.forEach(fn => fn(value));
            }
        } catch (_) {
            // Port closed or reader cancelled – expected on cleanup
        }
    }

    // Node.js stream .write(data, [encoding], [callback])
    write(data, encoding, callback) {
        if (typeof encoding === "function") {
            callback = encoding;
        }
        const bytes =
            data instanceof Uint8Array
                ? data
                : typeof data === "string"
                    ? new TextEncoder().encode(data)
                    : Uint8Array.from(data);

        this._writer
            .write(bytes)
            .then(() => callback && callback(null))
            .catch(err => callback && callback(err));
    }

    on(event, listener) {
        if (event === "data") this._dataListeners.push(listener);
        return this;
    }

    once(event, listener) {
        if (event === "data") this._onceListeners.push(listener);
        return this;
    }

    removeListener(event, listener) {
        if (event === "data") {
            this._dataListeners = this._dataListeners.filter(l => l !== listener);
            this._onceListeners = this._onceListeners.filter(l => l !== listener);
        }
        return this;
    }

    // Release locks so the underlying port can be closed
    async release() {
        try { await this._reader.cancel(); } catch (_) { }
        try { this._reader.releaseLock(); } catch (_) { }
        try { this._writer.releaseLock(); } catch (_) { }
    }
}


/**
 * Compiles an Arduino sketch using the remote compiler Lambda.
 *
 * @param {Array<{name: string, content: string}>} sketchFiles
 * @param {string} fqbn     – e.g. "arduino:avr:uno"
 * @param {Array}  libraries
 * @returns {Promise} resolves to parsed compiler result
 */
function compileArduinoSketch(sketchFiles, fqbn = "arduino:avr:uno", libraries = []) {

    const payload = { sketchFiles, fqbn, libraries };

    return api_gateway_conn
        .post(
            ARDUINO_COMPILER_URL,
            payload
        )
        .then((response) => {
            const raw = response.data;
            if (raw && typeof raw.body === "string") {
                try { raw.body = JSON.parse(raw.body); } catch (_) { }
            }
            return raw;
        });
}

/**
 * Compiles a sketch for an ESP8266 board using the ESP compiler Lambda.
 * Resolves to { success, bin } where bin is the base64 full flash image (written at 0x0).
 *
 * @param {Array<{name: string, content: string}>} sketchFiles
 * @param {string} fqbn     – e.g. "esp8266:esp8266:nodemcuv2"
 * @param {Array}  libraries
 * @returns {Promise} resolves to parsed compiler result
 */
function compileEspSketch(sketchFiles, fqbn = "esp8266:esp8266:nodemcuv2", libraries = []) {

    const payload = { sketchFiles, fqbn, libraries };

    // The emulator expects the API Gateway event shape ({ body: "<json string>" }) and always
    // answers HTTP 200 with { statusCode, body }, so errors come back as success: false here.
    const request = USE_LOCAL_ESP_COMPILER
        ? axios.post(ESP_COMPILER_LOCAL_URL, { body: JSON.stringify(payload) })
        : api_gateway_conn.post(ESP_COMPILER_URL, payload);

    return request
        .then((response) => {
            const raw = response.data;
            if (raw && typeof raw.body === "string") {
                try { raw.body = JSON.parse(raw.body); } catch (_) { }
            }
            return raw;
        });
}

/**
 * Compiles a sketch with the compiler that matches the board's platform.
 *
 * @param {Array<{name: string, content: string}>} sketchFiles
 * @param {string} boardId  – key of SUPPORTED_BOARDS
 * @param {Array}  libraries
 * @returns {Promise} resolves to parsed compiler result
 */
function compileSketchForBoard(sketchFiles, boardId, libraries = []) {
    const board = SUPPORTED_BOARDS[boardId];
    if (board.platform === "esp8266") {
        return compileEspSketch(sketchFiles, board.fqbn, libraries);
    }
    return compileArduinoSketch(sketchFiles, board.fqbn, libraries);
}

/**
 * Returns the board's serial port: the first previously authorized port, otherwise the
 * browser's port picker. requestPort() needs a user gesture, so call this directly from the
 * click handler, before any slow work like compiling.
 *
 * @returns {Promise<SerialPort>}
 */
async function selectSerialPort() {
    const existingPorts = await navigator.serial.getPorts();
    if (existingPorts.length > 0) {
        return existingPorts[0];
    }
    return navigator.serial.requestPort({ filters: ARDUINO_USB_FILTERS });
}

/**
 * Uploads a compiled Intel HEX (base64-encoded) to an Arduino over Web Serial.
 *
 * @param {string}   hexBase64       – Base64-encoded Intel HEX string from the compiler
 * @param {Object}   stkConfig       – STK500 configuration object for the target board
 * @param {Function} onStatusUpdate  – Callback(msg: string) to stream progress to the UI
 * @param {SerialPort} [selectedPort] – Port chosen at click time via selectSerialPort()
 * @returns {Promise<void>}
 */
async function uploadHexToBoard(hexBase64, stkConfig, onStatusUpdate = () => { }, selectedPort = null) {
    const MAX_UPLOAD_ATTEMPTS = 3;
    const RESET_DELAYS = [300, 500, 800]; // Progressive delays (ms) for each attempt

    // Decode base64 → raw Intel HEX ASCII text
    const hexText = atob(hexBase64);

    let stream = null;
    let port;
    try {

        // 1. Use the port picked at click time (falls back to selecting one now)
        port = selectedPort || await selectSerialPort();

        // 2. Open at the bootloader baud rate
        await port.open({ baudRate: stkConfig.baudRate });
        onStatusUpdate("Port opened.\n");

        // Create the stream once so we don't abort the underlying port readable on retries
        stream = new WebSerialStream(port);

        // 3. Flash with retry logic – re-trigger the bootloader between attempts
        let lastError = null;

        for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
            const resetDelay = RESET_DELAYS[attempt - 1] || RESET_DELAYS[RESET_DELAYS.length - 1];

            // DTR/RTS reset → triggers the bootloader
            if (attempt === 1) {
                onStatusUpdate("Resetting board…\n");
            } else {
                onStatusUpdate(`\nRetrying upload (attempt ${attempt}/${MAX_UPLOAD_ATTEMPTS})…\n`);
                onStatusUpdate("Re-triggering bootloader…\n");
            }

            await port.setSignals({ dataTerminalReady: false, requestToSend: false });
            await _delay(resetDelay);
            await port.setSignals({ dataTerminalReady: true, requestToSend: true });
            // Wait for the bootloader to initialize after reset — many clones need ≥250ms
            await _delay(250);

            // Drain any stale bytes the bootloader may have sent during reset
            await _delay(50);
            stream._dataListeners = [];
            stream._onceListeners = [];

            // Flash via STK500 protocol
            onStatusUpdate("Flashing sketch…\n");
            const programmer = new STK500(stream, stkConfig);

            try {
                await programmer.bootload(hexText);
                onStatusUpdate("\n✓ Upload complete!\n");
                lastError = null;
                break; // Success – exit retry loop
            } catch (error) {
                lastError = error;
                if (attempt < MAX_UPLOAD_ATTEMPTS) {
                    onStatusUpdate(`\n⚠ Attempt ${attempt} failed: ${error.message}. Retrying…\n`);
                }
            }
        }

        if (lastError) {
            onStatusUpdate(`\n✗ Upload Failed: ${lastError.message}\n`);
            throw new Error(`Upload Failed: ${lastError.message}`);
        }
    } finally {
        // Release stream locks before closing the port
        if (stream) await stream.release();
        await port.close().catch(() => { });
    }
}

/**
 * Flashes a compiled ESP8266 image (base64-encoded .bin) over Web Serial using esptool-js.
 *
 * @param {string}   binBase64       – Base64-encoded full flash image from the ESP compiler
 * @param {Object}   espConfig       – ESP flashing configuration for the target board
 * @param {Function} onStatusUpdate  – Callback(msg: string) to stream progress to the UI
 * @param {SerialPort} [selectedPort] – Port chosen at click time via selectSerialPort()
 * @returns {Promise<void>}
 */
async function uploadBinToEspBoard(binBase64, espConfig, onStatusUpdate = () => { }, selectedPort = null) {
    // Decode base64 → raw firmware bytes
    const binString = atob(binBase64);
    const binData = new Uint8Array(binString.length);
    for (let i = 0; i < binString.length; i++) {
        binData[i] = binString.charCodeAt(i);
    }

    let transport = null;
    try {

        // 1. Use the port picked at click time (falls back to selecting one now)
        const port = selectedPort || await selectSerialPort();

        // 2. Connect: esptool-js opens the port and resets the chip into its bootloader
        transport = new Transport(port);
        const terminal = {
            clean: () => { },
            writeLine: (data) => onStatusUpdate(data + "\n"),
            write: (data) => onStatusUpdate(data),
        };
        const loader = new ESPLoader({
            transport,
            baudrate: espConfig.baudRate,
            terminal,
        });

        onStatusUpdate("Connecting to ESP bootloader…\n");
        const chip = await loader.main();
        onStatusUpdate(`Connected to ${chip}.\n`);

        // 3. Flash the full image; "keep" leaves the compiler's flash header settings untouched
        onStatusUpdate("Flashing sketch…\n");
        let lastReportedPct = -1;
        await loader.writeFlash({
            fileArray: [{ data: binData, address: espConfig.flashAddress }],
            flashMode: "keep",
            flashFreq: "keep",
            flashSize: "keep",
            eraseAll: false,
            compress: true,
            reportProgress: (_fileIndex, written, total) => {
                const pct = Math.floor((written / total) * 100);
                if (pct >= lastReportedPct + 10 || pct === 100) {
                    lastReportedPct = pct;
                    onStatusUpdate(`Writing… ${pct}%\n`);
                }
            },
        });

        // 4. Reset the board so the new sketch starts running
        await loader.after("hard_reset");
        onStatusUpdate("\n✓ Upload complete!\n");
    } catch (error) {
        onStatusUpdate(`\n✗ Upload Failed: ${error.message}\n`);
        throw new Error(`Upload Failed: ${error.message}`);
    } finally {
        if (transport) await transport.disconnect().catch(() => { });
    }
}

function _delay(ms) {
    return new Promise((res) => setTimeout(res, ms));
}

export { compileArduinoSketch, compileEspSketch, compileSketchForBoard, uploadHexToBoard, uploadBinToEspBoard, selectSerialPort, getCodeSuggestions, SUPPORTED_BOARDS, ARDUINO_USB_FILTERS };
